import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, X, Wifi, Upload, Download, Shield, Clock } from 'lucide-react';
import { useDarkMode } from '../../../darkmode/DarkModeContext';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  'http://localhost:5000';

const BroadcastPackages = () => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate(); 
  const [activeTab, setActiveTab] = useState('broadcast'); // 'broadcast' | 'streaming' | 'enterprise'
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // --- helpers untuk kecepatan dan spesifikasi teknis ---
  const resolveUpload = (pkg) => {
    if (pkg.upload) return pkg.upload;
    const v = pkg?.specifications?.uploadSpeed?.value;
    const u = pkg?.specifications?.uploadSpeed?.unit || 'Mbps';
    return typeof v === 'number' ? `${v} ${u}` : '-';
  };
  const resolveDownload = (pkg) => {
    if (pkg.speed) return pkg.speed; // sudah string "100 Mbps"
    const v = pkg?.specifications?.downloadSpeed?.value;
    const u = pkg?.specifications?.downloadSpeed?.unit || 'Mbps';
    return typeof v === 'number' ? `${v} ${u}` : '-';
  };
  const resolveTechSpecs = (pkg) => {
    // Backend broadcast seed kamu tidak menyimpan technicalSpecs (opsional).
    // Kita derive dari specifications jika perlu.
    if (pkg.technicalSpecs && Object.keys(pkg.technicalSpecs).length) return pkg.technicalSpecs;
    const spec = pkg.specifications || {};
    const out = {};
    if (spec?.latency?.value != null) out['Latency'] = `< ${spec.latency.value}${spec.latency.unit || 'ms'}`;
    // contoh tambahan; kalau nanti kamu simpan jitter/packetLoss di DB, otomatis tampil
    if (spec?.jitter?.value != null) out['Jitter'] = `< ${spec.jitter.value}${spec.jitter.unit || 'ms'}`;
    if (spec?.packetLoss?.value != null) out['Packet Loss'] = `< ${spec.packetLoss.value}${spec.packetLoss.unit || '%'}`;
    if (!Object.keys(out).length) return {}; // biar map nggak error
    return out;
  };

  // --- fetch dari API ---
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await fetch(`${API_BASE}/api/packages?category=${activeTab}`, { signal: ac.signal });
        const json = await res.json();
        if (!json?.success) throw new Error(json?.message || 'Gagal mengambil paket');
        setPackages(Array.isArray(json.data) ? json.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message || 'Terjadi kesalahan jaringan');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [activeTab]);

  const currentPackages = useMemo(() => packages, [packages]);

  const handlePackageSelect = (pkg) => setSelectedPackage(pkg);
  const closePackageDetail = () => setSelectedPackage(null);

  // Normalisasi paket agar aman dipakai di CoverageCheck
  const normalizeForCheckout = (pkg) => {
    const id = pkg._id || pkg.id || `pkg_${Date.now()}`;
    return {
      _id: id,
      id,
      name: pkg.name,
      speed: resolveDownload(pkg),         // "100 Mbps", dsb
      upload: resolveUpload(pkg),          // opsional
      features: pkg.features || [],
      category: pkg.category || 'broadcast',
      color: pkg.color
    };
  };

  const handleGoToCoverage = (pkg) => {
    const normalized = normalizeForCheckout(pkg);
    // sesuaikan path dengan routing kamu: '/coverage-check' atau '/coveragecheck'
    navigate('/coverage-check', { state: { selectedPackage: normalized } });
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center transition-colors duration-300"
      style={{
        backgroundImage: "url('https://telnet.net.id/assets/bg.png')",
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
      {/* Custom Scrollbar Styles */}
      <style>
        {`
          /* Scrollbar styling for webkit browsers */
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            background: ${isDarkMode ? '#374151' : '#e5e7eb'};
            border-radius: 10px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDarkMode ? '#1E9B4C' : '#10b981'};
            border-radius: 10px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDarkMode ? '#15803d' : '#059669'};
          }
          
          /* Scrollbar styling for firefox */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: ${isDarkMode ? '#1E9B4C #374151' : '#10b981 #e5e7eb'};
          }
          
          /* Smooth scrolling */
          .smooth-scroll {
            scroll-behavior: smooth;
          }
        `}
      </style>

      {/* Responsive Tabs */}
      <div
        className={`
          py-4 sm:py-6 px-4 border-b transition-colors
          ${isDarkMode ? 'bg-[#0b1114] border-gray-700' : 'bg-transparent border-gray-200'}
        `}
      >
        <div className="container mx-auto">
          <div className="flex justify-center overflow-x-auto">
            {/* Mobile: horizontal scrolling tabs */}
            <div className={`flex space-x-1 p-1 rounded-lg min-w-max ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              {[{
                id: 'broadcast',
                name: 'Broadcast',
                icon: <Wifi className="w-4 h-4" />
              },
              {
                id: 'streaming',
                name: 'Streaming',
                icon: <Upload className="w-4 h-4" />
              },
              {
                id: 'enterprise',
                name: 'Enterprise',
                icon: <Shield className="w-4 h-4" />
              }].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 
                    rounded-lg font-medium transition-all duration-200 whitespace-nowrap
                    text-sm sm:text-base
                    ${activeTab === tab.id
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                    }
                  `}
                >
                  {tab.icon}
                  <span className="hidden xs:inline sm:inline">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cards Section with Mobile Responsive Design */}
      <section className="py-8 sm:py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 transition-colors ${isDarkMode ? 'text-green-50' : 'text-gray-900'}`}>
              Paket {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h2>
            <p className={`text-base sm:text-lg transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Pilih paket yang sesuai dengan kebutuhan Anda
            </p>
          </div>

          {loading && <div className={`text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Memuat paket…</div>}
          {err && <div className="text-center text-red-500">{err}</div>}

          {!loading && !err && (
            /* Mobile: Horizontal scroll container */
            <div className="overflow-x-auto pb-4 custom-scrollbar smooth-scroll">
              <div className="flex gap-4 sm:gap-6 lg:gap-8" style={{ width: 'max-content' }}>
                {currentPackages.map((pkg) => {
                  const uploadText = resolveUpload(pkg);
                  const downloadText = resolveDownload(pkg);

                  return (
                    <div
                      key={pkg._id}
                      className={`
                        relative rounded-2xl p-6 sm:p-8 shadow-lg transition-all duration-300 
                        hover:scale-105 flex-shrink-0
                        w-72 sm:w-80 lg:w-96
                        ${pkg.popular
                          ? `bg-gradient-to-br ${pkg.color || 'from-green-500 to-green-700'} text-white shadow-2xl transform scale-105`
                          : isDarkMode
                          ? 'bg-gray-800 text-green-50 border border-gray-700'
                          : 'bg-white text-gray-900 border border-gray-200'
                        }
                      `}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-yellow-400 text-gray-900 px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-bold">
                            Most Popular
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h3 className="text-xl sm:text-2xl font-bold mb-2">{pkg.name}</h3>
                      </div>

                      <div className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                          <span className="flex items-center gap-1 text-sm">
                            <Download className="w-4 h-4" />
                            Download
                          </span>
                          <span className="font-bold text-sm">{downloadText}</span>
                        </div>
                        <div className="flex items-center justify-between mb-4">
                          <span className="flex items-center gap-1 text-sm">
                            <Upload className="w-4 h-4" />
                            Upload
                          </span>
                          <span className="font-bold text-sm">{uploadText}</span>
                        </div>

                        <ul className="space-y-2 sm:space-y-3 max-h-32 sm:max-h-40 overflow-y-auto custom-scrollbar">
                          {(pkg.features || []).map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-[#1E9B4C] flex-shrink-0 mt-0.5" />
                              <span className="text-xs sm:text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <button
                        onClick={() => handlePackageSelect(pkg)}
                        className={`
                          w-full py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold 
                          transition-colors duration-200 text-sm sm:text-base
                          ${pkg.popular 
                            ? 'bg-white text-green-700 hover:bg-gray-100' 
                            : 'bg-[#1E9B4C] text-white hover:bg-green-700'
                          }
                        `}
                      >
                        Pilih Paket
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Scroll indicator for mobile */}
          {!loading && !err && currentPackages.length > 0 && (
            <div className="text-center mt-4 sm:hidden">
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ← Geser untuk melihat paket lainnya →
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Responsive Modal */}
      {selectedPackage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[2001]">
          <div className={`
            w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl custom-scrollbar smooth-scroll
            ${isDarkMode ? 'bg-gray-800 text-green-50' : 'bg-white text-gray-900'}
          `}>
            <div className={`
              sticky top-0 flex justify-between items-center p-4 sm:p-6 border-b 
              ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}
            `}>
              <h3 className="text-xl sm:text-2xl font-bold pr-4">Detail Paket {selectedPackage.name}</h3>
              <button 
                onClick={() => setSelectedPackage(null)} 
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X className="w-5 sm:w-6 h-5 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {/* Overview */}
                <div>
                  <div className={`p-4 sm:p-6 rounded-xl mb-6 bg-gradient-to-br ${selectedPackage.color || 'from-green-500 to-green-700'} text-white`}>
                    <h4 className="text-lg sm:text-xl font-bold mb-2">{selectedPackage.name}</h4>
                    <p className="opacity-90 text-sm sm:text-base">{selectedPackage.description}</p>
                  </div>

                  <div className={`p-4 sm:p-6 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <Wifi className="w-4 sm:w-5 h-4 sm:h-5" />
                      <span className="text-sm sm:text-base">Kecepatan Internet</span>
                    </h5>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm sm:text-base">Download Speed</span>
                        <span className="font-bold text-[#1E9B4C] text-sm sm:text-base">{selectedPackage._download || resolveDownload(selectedPackage)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm sm:text-base">Upload Speed</span>
                        <span className="font-bold text-[#1E9B4C] text-sm sm:text-base">{selectedPackage._upload || resolveUpload(selectedPackage)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features & Specs */}
                <div className="space-y-6">
                  <div className={`p-4 sm:p-6 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-[#1E9B4C]" />
                      <span className="text-sm sm:text-base">Fitur Unggulan</span>
                    </h5>
                    <ul className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {(selectedPackage.features || []).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-3 sm:w-4 h-3 sm:h-4 text-[#1E9B4C] flex-shrink-0 mt-0.5" />
                          <span className="text-xs sm:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={`p-4 sm:p-6 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <Clock className="w-4 sm:w-5 h-4 sm:h-5 text-[#1E9B4C]" />
                      <span className="text-sm sm:text-base">Spesifikasi Teknis</span>
                    </h5>
                    <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar">
                      {Object.entries(selectedPackage._tech || resolveTechSpecs(selectedPackage)).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-xs sm:text-sm">{key}</span>
                          <span className="font-bold text-xs sm:text-sm">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => handleGoToCoverage(selectedPackage)}
                  className="flex-1 bg-[#1E9B4C] text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors text-sm sm:text-base"
                >
                  Berlangganan Sekarang
                </button>
                <button className={`
                  px-6 py-3 rounded-lg font-semibold border transition-colors text-sm sm:text-base
                  ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}
                `}>
                  Konsultasi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastPackages;